import "./SystemBannersTab.scss";
import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import FlowButton from "../../../components/FlowButton/FlowButton";
import FlowCheckbox from "../../../components/FlowCheckbox/FlowCheckbox";
import { SystemBanner } from "../../../components/SystemBanners/SystemBanners";
import { useSocket } from "../../../context/SocketContext";
import { getAllSystemBanners } from "../../../api/systemBanners/getAll";
import {
  createSystemBanner,
  updateSystemBanner,
  reshowSystemBanner,
  deleteSystemBanner,
} from "../../../api/systemBanners/save";
import {
  BANNER_ICONS,
  BANNER_COLORS,
  isBannerLive,
} from "../../../utils/systemBanners";

const EMPTY_BANNER = {
  text: "",
  icon: "info",
  color: "info",
  dismissible: true,
  isActive: true,
  startsAt: "",
  endsAt: "",
};

// Date <-> value of an <input type="datetime-local"> (local time)
const toInputValue = (date) => {
  if (!date) return "";
  const d = new Date(date);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const fromInputValue = (value) =>
  value ? new Date(value).toISOString() : null;

// Live, scheduled, ended or switched off
const getBannerStatus = (banner) => {
  if (!banner.isActive) return "off";
  if (isBannerLive(banner)) return "live";
  if (banner.endsAt && new Date(banner.endsAt) <= new Date()) return "ended";
  return "scheduled";
};

// Super admin: banners shown at the top of the apps for all users
function SystemBannersTab() {
  const { t, i18n } = useTranslation();
  const { socket } = useSocket();
  const [banners, setBanners] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [form, setForm] = useState(EMPTY_BANNER);
  const [editingId, setEditingId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [reshownId, setReshownId] = useState(null);

  const loadBanners = useCallback(async () => {
    try {
      setBanners(await getAllSystemBanners());
    } catch (err) {
      console.error("Error fetching system banners:", err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadBanners();
  }, [loadBanners]);

  // Changes made by other super admins
  useEffect(() => {
    if (!socket) return;
    socket.on("systemBanners:changed", loadBanners);
    return () => socket.off("systemBanners:changed", loadBanners);
  }, [socket, loadBanners]);

  const setField = (key, value) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const resetForm = () => {
    setForm(EMPTY_BANNER);
    setEditingId(null);
    setError(null);
  };

  const handleEdit = (banner) => {
    setEditingId(banner._id);
    setError(null);
    setForm({
      text: banner.text,
      icon: banner.icon,
      color: banner.color,
      dismissible: banner.dismissible,
      isActive: banner.isActive,
      startsAt: toInputValue(banner.startsAt),
      endsAt: toInputValue(banner.endsAt),
    });
  };

  const handleSave = async () => {
    if (!form.text.trim()) {
      setError(t("superAdmin.banners.textRequired"));
      return;
    }
    if (form.startsAt && form.endsAt && form.endsAt <= form.startsAt) {
      setError(t("superAdmin.banners.endBeforeStart"));
      return;
    }

    const banner = {
      ...form,
      text: form.text.trim(),
      startsAt: fromInputValue(form.startsAt),
      endsAt: fromInputValue(form.endsAt),
    };

    setIsSaving(true);
    setError(null);
    try {
      if (editingId) {
        await updateSystemBanner(editingId, banner);
      } else {
        await createSystemBanner(banner);
      }
      resetForm();
      loadBanners();
    } catch (err) {
      console.error("Error saving system banner:", err);
      setError(t("superAdmin.banners.saveFailed"));
    } finally {
      setIsSaving(false);
    }
  };

  const handleReshow = async (banner) => {
    try {
      await reshowSystemBanner(banner._id);
      // Short confirmation on the button
      setReshownId(banner._id);
      setTimeout(
        () => setReshownId((id) => (id === banner._id ? null : id)),
        2000,
      );
    } catch (err) {
      console.error("Error showing system banner again:", err);
    }
  };

  const handleToggleActive = async (banner) => {
    try {
      await updateSystemBanner(banner._id, { isActive: !banner.isActive });
      loadBanners();
    } catch (err) {
      console.error("Error updating system banner:", err);
    }
  };

  const handleDelete = async (banner) => {
    if (!window.confirm(t("superAdmin.banners.deleteConfirm"))) return;
    try {
      await deleteSystemBanner(banner._id);
      if (editingId === banner._id) resetForm();
      loadBanners();
    } catch (err) {
      console.error("Error deleting system banner:", err);
    }
  };

  const formatDate = (date) =>
    new Date(date).toLocaleString(i18n.language, {
      dateStyle: "medium",
      timeStyle: "short",
    });

  return (
    <div className="super-dashboard-content system-banners-tab">
      <h1>{t("superAdmin.banners.title")}</h1>
      <p className="system-banners-tab__intro">
        {t("superAdmin.banners.intro")}
      </p>

      <div className="system-banners-tab__columns">
        <div className="system-banners-tab__editor">
          <h2>
            {editingId
              ? t("superAdmin.banners.editTitle")
              : t("superAdmin.banners.newTitle")}
          </h2>

          <label className="system-banners-tab__label">
            {t("superAdmin.banners.text")}
          </label>
          <textarea
            className="system-banners-tab__textarea"
            value={form.text}
            maxLength={500}
            rows={3}
            placeholder={t("superAdmin.banners.textPlaceholder")}
            onChange={(e) => setField("text", e.target.value)}
          />

          <label className="system-banners-tab__label">
            {t("superAdmin.banners.icon")}
          </label>
          <div className="system-banners-tab__icons">
            {BANNER_ICONS.map((icon) => (
              <button
                key={icon}
                type="button"
                className={`system-banners-tab__icon ${form.icon === icon ? "is-selected" : ""}`}
                onClick={() => setField("icon", icon)}
                title={icon}
              >
                <span className="material-symbols-outlined">{icon}</span>
              </button>
            ))}
          </div>

          <label className="system-banners-tab__label">
            {t("superAdmin.banners.color")}
          </label>
          <div className="system-banners-tab__colors">
            {BANNER_COLORS.map((color) => (
              <button
                key={color}
                type="button"
                className={`system-banners-tab__color system-banners-tab__color--${color} ${form.color === color ? "is-selected" : ""}`}
                onClick={() => setField("color", color)}
              >
                {t(`superAdmin.banners.colors.${color}`)}
              </button>
            ))}
          </div>

          <div className="system-banners-tab__dates">
            <div>
              <label className="system-banners-tab__label">
                {t("superAdmin.banners.startsAt")}
              </label>
              <input
                type="datetime-local"
                className="system-banners-tab__input"
                value={form.startsAt}
                onChange={(e) => setField("startsAt", e.target.value)}
              />
            </div>
            <div>
              <label className="system-banners-tab__label">
                {t("superAdmin.banners.endsAt")}
              </label>
              <input
                type="datetime-local"
                className="system-banners-tab__input"
                value={form.endsAt}
                onChange={(e) => setField("endsAt", e.target.value)}
              />
            </div>
          </div>
          <p className="system-banners-tab__hint">
            {t("superAdmin.banners.datesHint")}
          </p>

          <div className="system-banners-tab__checks">
            <FlowCheckbox
              checked={form.dismissible}
              onChange={(value) => setField("dismissible", value)}
              label={t("superAdmin.banners.dismissible")}
            />
            <FlowCheckbox
              checked={form.isActive}
              onChange={(value) => setField("isActive", value)}
              label={t("superAdmin.banners.active")}
            />
          </div>

          <label className="system-banners-tab__label">
            {t("superAdmin.banners.preview")}
          </label>
          <div className="system-banners-tab__preview">
            <SystemBanner
              banner={{
                ...form,
                text: form.text || t("superAdmin.banners.textPlaceholder"),
              }}
              onDismiss={() => {}}
            />
          </div>

          {error && <div className="system-banners-tab__error">{error}</div>}

          <div className="system-banners-tab__actions">
            {editingId && (
              <FlowButton onClick={resetForm} disabled={isSaving}>
                {t("common.cancel")}
              </FlowButton>
            )}
            <FlowButton
              variant="update"
              onClick={handleSave}
              disabled={isSaving}
            >
              {editingId
                ? t("superAdmin.banners.saveChanges")
                : t("superAdmin.banners.create")}
            </FlowButton>
          </div>
        </div>

        <div className="system-banners-tab__list">
          <h2>{t("superAdmin.banners.listTitle")}</h2>
          {!isLoading && banners.length === 0 && (
            <p className="system-banners-tab__hint">
              {t("superAdmin.banners.empty")}
            </p>
          )}
          {banners.map((banner) => {
            const status = getBannerStatus(banner);
            return (
              <div
                key={banner._id}
                className={`system-banners-tab__item ${editingId === banner._id ? "is-editing" : ""}`}
              >
                <SystemBanner banner={banner} />
                <div className="system-banners-tab__item-footer">
                  <span
                    className={`system-banners-tab__status system-banners-tab__status--${status}`}
                  >
                    {t(`superAdmin.banners.status.${status}`)}
                  </span>
                  <span className="system-banners-tab__meta">
                    {banner.startsAt &&
                      t("superAdmin.banners.from", {
                        date: formatDate(banner.startsAt),
                      })}{" "}
                    {banner.endsAt &&
                      t("superAdmin.banners.until", {
                        date: formatDate(banner.endsAt),
                      })}
                    {!banner.dismissible &&
                      ` · ${t("superAdmin.banners.notDismissible")}`}
                  </span>
                  <div className="system-banners-tab__item-actions">
                    {banner.dismissible && (
                      <button
                        onClick={() => handleReshow(banner)}
                        title={t("superAdmin.banners.reshow")}
                      >
                        <span className="material-symbols-outlined">
                          {reshownId === banner._id ? "check" : "replay"}
                        </span>
                      </button>
                    )}
                    <button
                      onClick={() => handleToggleActive(banner)}
                      title={
                        banner.isActive
                          ? t("superAdmin.banners.turnOff")
                          : t("superAdmin.banners.turnOn")
                      }
                    >
                      <span className="material-symbols-outlined">
                        {banner.isActive ? "visibility_off" : "visibility"}
                      </span>
                    </button>
                    <button
                      onClick={() => handleEdit(banner)}
                      title={t("common.edit")}
                    >
                      <span className="material-symbols-outlined">edit</span>
                    </button>
                    <button
                      onClick={() => handleDelete(banner)}
                      title={t("common.delete")}
                    >
                      <span className="material-symbols-outlined">delete</span>
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default SystemBannersTab;
